import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/common/Button';
import { usePageTitle } from '../hooks/usePageTitle';
import type { ErrorPageState } from '../types/errors';

/**
 * US11, US15 - Error Page (FR-115 to FR-127, FR-151 to FR-158)
 *
 * Displays a user-friendly error message when a server error occurs.
 * Shows contextual messages based on error type (v3.1).
 * Shows the error reference code for support purposes.
 * Notifies users that the technical team has been automatically notified (for 500 errors).
 */
export function ErrorPage() {
  usePageTitle('Error — Hawkes Bridge');
  const navigate = useNavigate();
  const location = useLocation();

  const [errorState, setErrorState] = useState<ErrorPageState>({
    referenceCode: 'Unknown',
    canRetry: true,
    returnUrl: '/dashboard',
  });

  // Get error details from router state or sessionStorage
  useEffect(() => {
    // First try router state
    const routerState = location.state as ErrorPageState | undefined;
    if (routerState?.referenceCode) {
      setErrorState(routerState);
      return;
    }

    // Fall back to sessionStorage (used by window.location redirects)
    const storedState = sessionStorage.getItem('redirectState');
    if (storedState) {
      try {
        const parsed = JSON.parse(storedState) as ErrorPageState;
        setErrorState({
          referenceCode: parsed.referenceCode || 'Unknown',
          correlationId: parsed.correlationId,
          canRetry: parsed.canRetry ?? true,
          returnUrl: parsed.returnUrl || '/dashboard',
          message: parsed.message,
          errorType: parsed.errorType,
        });
      } catch {
        // Ignore parse errors
      }
      // Clean up stored state
      sessionStorage.removeItem('redirectState');
    }
  }, [location.state]);

  const { referenceCode, canRetry, returnUrl, message, errorType } = errorState;

  // Determine if this is a server error (500) that notifies the technical team
  const isServerError = errorType === 'server-error' || (!errorType && referenceCode !== 'NOT_FOUND' && referenceCode !== 'GATEWAY_ERROR' && referenceCode !== 'TIMEOUT' && referenceCode !== 'NETWORK_ERROR');

  // Get contextual title based on error type (v3.1)
  const getTitle = () => {
    switch (errorType) {
      case 'not-found':
        return 'Page Not Found';
      case 'gateway-error':
        return 'Server Unavailable';
      case 'timeout':
        return 'Request Timeout';
      case 'network-error':
        return 'Connection Error';
      default:
        return 'Something Went Wrong';
    }
  };

  // Get default message if not provided
  const getDefaultMessage = () => {
    switch (errorType) {
      case 'not-found':
        return 'The requested page or resource could not be found.';
      case 'gateway-error':
        return 'The server is temporarily unavailable. Please try again in a few moments.';
      case 'timeout':
        return 'The server took too long to respond. Please try again.';
      case 'network-error':
        return 'Unable to connect to the server. Please check your internet connection.';
      default:
        return 'We encountered an unexpected error while processing your request. Please try again.';
    }
  };

  // Get icon color based on error type
  const getIconColors = () => {
    switch (errorType) {
      case 'network-error':
        return { bg: 'bg-yellow-100', icon: 'text-yellow-600' };
      case 'gateway-error':
      case 'timeout':
        return { bg: 'bg-orange-100', icon: 'text-orange-600' };
      default:
        return { bg: 'bg-red-100', icon: 'text-red-600' };
    }
  };

  const iconColors = getIconColors();

  const handleRetryClick = () => {
    // Navigate back to the return URL to retry the operation
    navigate(returnUrl || '/dashboard');
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleDashboardClick = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="max-w-lg w-full mx-4">
        <div className="bg-card rounded-lg shadow-lg p-8 text-center">
          {/* Error Icon - color based on error type */}
          <div className={`mx-auto w-16 h-16 ${iconColors.bg} rounded-full flex items-center justify-center mb-6`}>
            {errorType === 'network-error' ? (
              <svg
                className={`w-8 h-8 ${iconColors.icon}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
                />
              </svg>
            ) : errorType === 'not-found' ? (
              <svg
                className={`w-8 h-8 ${iconColors.icon}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ) : (
              <svg
                className={`w-8 h-8 ${iconColors.icon}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            )}
          </div>

          {/* Heading - contextual based on error type (v3.1) */}
          <h1 className="text-2xl font-bold text-ink mb-2">{getTitle()}</h1>

          {/* User-friendly message - FR-116, FR-151-154 */}
          <p className="text-ink-soft mb-4">
            {message || getDefaultMessage()}
          </p>

          {/* Technical team notification message - FR-119 (only for server errors) */}
          {isServerError && (
            <div className="bg-primary-50 border border-primary-200 rounded-md p-4 mb-6">
              <div className="flex items-start">
                <svg
                  className="w-5 h-5 text-primary-500 mt-0.5 mr-3 flex-shrink-0"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-primary-700 text-left">
                  Our technical team has been automatically notified and is working to resolve this issue.
                </p>
              </div>
            </div>
          )}

          {/* Error Reference Code - FR-117 (only show for server errors with real reference codes) */}
          {isServerError && referenceCode && referenceCode !== 'Unknown' && (
            <div className="bg-cream rounded-md p-4 mb-6">
              <p className="text-sm text-ink-soft mb-1">Error Reference Code</p>
              <p
                className="font-mono text-lg font-semibold text-ink"
                data-testid="error-reference-code"
              >
                {referenceCode}
              </p>
              <p className="text-xs text-ink-soft mt-2">
                Please quote this code if you need to contact support.
              </p>
            </div>
          )}

          {/* For non-server errors, show a simple reference if needed */}
          {!isServerError && (
            <div className="mb-6">
              <p
                className="text-xs text-ink-soft font-mono"
                data-testid="error-reference-code"
              >
                {referenceCode}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="space-y-3">
            {/* Retry button - only show if retry is allowed */}
            {canRetry && (
              <Button
                onClick={handleRetryClick}
                variant="primary"
                className="w-full"
                data-testid="retry-button"
              >
                Try Again
              </Button>
            )}

            {/* Dashboard button */}
            <Button
              onClick={handleDashboardClick}
              variant={canRetry ? 'secondary' : 'primary'}
              className="w-full"
              data-testid="go-dashboard-button"
            >
              Go to Dashboard
            </Button>

            {/* Home button */}
            <Button
              onClick={handleHomeClick}
              variant="secondary"
              className="w-full"
              data-testid="go-home-button"
            >
              Go to Home Page
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
